import { Alert, Box, Button, Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormWrapper, } from "../../../components/customStyles/form";
import UserFormFields from "../../../components/input/form/UserFormFields";
import EditOnly from "../../../components/permissions/EditOnly";
import { ELanguage } from "../../../models/enums/language";
import { ERole } from "../../../models/enums/role";
import { IUserGroup } from "../../../models/propertyGroup";
import IUserRegistrationRequest from "../../../models/requests/user/userRegistrationRequest";
import IUser from "../../../models/user";
import propertyGroupService from "../../../services/propertyGroupService/PropertyGroupService";
import UserService from "../../../services/user/UserService";
import { validEmailRegex, validUserCodeRegex } from "../../../utils/regex";
import { IUpdatedUser } from "./UserEditPage";
import WorkSchedulesSection, { normalizeToPayload } from "../../../components/input/schedules/WorkSchedulesSection";
import { IWorkSchedule } from "../../../models/workSchedule";
import WorkScheduleService from "../../../services/workSchedule/WorkScheduleService";
import { Create } from "@mui/icons-material";

export interface IUserState extends IUserRegistrationRequest {
    isFormSending: boolean;
    firstName?: string;
    lastName?: string;
    managerId: number;
    buildingIds?: number[];
    propertyGroupIds?: number[];
    microsoftId?: string;
    acombaEmployeeId?: number;
    acombaFunctionNumber?: number;
}

export const usableRoles = [
    ERole.Admin,
    ERole.Manager,
    ERole.Supervisor,
    ERole.Client,
    ERole.LimitedClient,
    ERole.Employee,
];

const UserAddPage = () => {
    const { t } = useTranslation();

    const [isErrorMessageVisible, setIsErrorMessageVisible] = useState<boolean>(false);

    const [managers, setManagers] = useState<IUser[]>([]);
    const [propertyGroups, setPropertyGroups] = useState<IUserGroup[]>([]);

    const navigate = useNavigate();
    const [userState, setUserState] = useState<IUserState>({
        name: "",
        code: "",
        firstName: "",
        lastName: "",
        email: "",
        isAgreeWithTerms: true,
        isAgreeWithNewsletter: false,
        language: ELanguage.English,
        role: ERole.Employee,
        managerId: 0,
        isFormSending: false,
        buildingIds: [],
    });

    const [workSchedules, setWorkSchedules] = useState<IWorkSchedule[]>([]);

    useEffect(() => {
        UserService.GetUsersByCompany().then((users) => {
            setManagers(users.filter((user) => user.role === ERole.Admin || user.role === ERole.Client || user.role === ERole.LimitedClient));
        });

        propertyGroupService.GetPropertyGroups().then((propertyGroups) => {
            const userGroups: IUserGroup[] = propertyGroups.map((propertyGroup) => ({
                id: propertyGroup.id,
                name: propertyGroup.name,
                userId: 0,
                propertyGroupId: propertyGroup.id
            }));

            setPropertyGroups(userGroups);
        });
    }, []);


    const isValidForm = () => {
        if (!validUserCodeRegex.test(userState.code || ""))
            return false;

        if (
            !userState.firstName?.trim() || !userState.lastName?.trim()
        )
            return false;
        if (
            (userState.role === ERole.Admin || userState.role === ERole.Manager || userState.role === ERole.Supervisor || userState.role === ERole.Client || userState.role === ERole.LimitedClient) &&
            !validEmailRegex.test(userState.email!)
        )
            return false;
        if (userState.role === ERole.Employee && !userState.managerId)
            return false;

        if (
            workSchedules.length !== 0 &&
            workSchedules.some(s =>
                s.days.some(
                    d =>
                        !d.isHoliday &&
                        (
                            !d.startTime ||
                            !d.endTime ||
                            d.startTime === d.endTime ||
                            (d.breakMinutes === undefined || d.breakMinutes === null || d.breakMinutes < 0)
                        )
                )
            )
        ) {
            return false;
        }
        return true;
    };


    const onCreateUser = async () => {
        setUserState((prevState) => ({
            ...prevState,
            isFormSending: true,
        }));

        try {
            const newUser = await UserService.CreateUser(
                userState.name,
                userState.code || "",
                userState.isManualCode || false,
                userState.isAgreeWithTerms,
                userState.isAgreeWithNewsletter,
                userState.language,
                userState.email || undefined,
                undefined,
                userState.role,
                userState.managerId || undefined,
                userState.propertyGroupIds,
                userState.acombaEmployeeId || undefined,
                userState.acombaFunctionNumber || undefined,
            );
            if (!newUser) {
                setIsErrorMessageVisible(true);
                return;
            }

            await saveSchedules(newUser.id);

            navigate("../edit", { state: { ...newUser }, replace: true });
        } catch (e) {
            setIsErrorMessageVisible(true);
        } finally {
            setUserState((prevState) => ({
                ...prevState,
                isFormSending: false,
            }));
        }
    };

    const saveSchedules = async (userId: number) => {
        if (!userId || !workSchedules.length) return;

        await WorkScheduleService.UpsertMany(userId, workSchedules.map(schedule => normalizeToPayload({ ...schedule, userId })));
    };

    return (
        <>
            <Box sx={(theme) => ({
                position: "sticky",
                top: -16,
                zIndex: theme.zIndex.appBar,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                p: 1,
                backgroundColor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
            })}>
                <EditOnly>
                    <Button
                        startIcon={<Create />}
                        onClick={() => onCreateUser()}
                        variant="contained"
                        color="primary"
                        disabled={
                            !isValidForm()}
                    >
                        {t("table.user.create")}
                    </Button>
                </EditOnly>
            </Box>
            <Box component={Paper} sx={{ width: "100%", pb: 2 }}>
                <FormWrapper>
                    {isErrorMessageVisible && (
                        <Alert
                            severity="error"
                            sx={{ marginBottom: "1em" }}
                        >
                            {t("table.user.addErrorMessage")}
                        </Alert>
                    )}

                    <UserFormFields
                        userState={userState}
                        setUserState={
                            setUserState as React.Dispatch<
                                React.SetStateAction<
                                    IUserState | IUpdatedUser
                                >
                            >
                        }
                        managers={managers}
                        propertyGroups={propertyGroups}
                    ></UserFormFields>
                </FormWrapper>
            </Box>

            <Box component={Paper} sx={{ width: "100%", marginTop: 4 }}>
                <WorkSchedulesSection
                    userId={0}
                    propertyGroups={propertyGroups}
                    propertyGroupIds={userState.propertyGroupIds}
                    onSchedulesChange={setWorkSchedules}
                />
            </Box>

        </>
    );
};

export default UserAddPage;
